import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ServiceService } from '../../../services/service.service';
import { LocationContextService } from '../../../services/location-context.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Component({
    selector: 'app-add-store',
    templateUrl: './add-store.component.html',
    styleUrls: ['./add-store.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, CommonModule, MatSnackBarModule]
})
export class AddStoreComponent implements OnInit {
  @Input() companyId!: string;
  @Output() storeAdded = new EventEmitter<void>();

  addStoreForm = new FormGroup({
    storeName: new FormControl('', [Validators.required, Validators.minLength(2)]),
    storeAddress: new FormControl('', [Validators.required]),
    storeCity: new FormControl('', [Validators.required]),
    storePhone: new FormControl('', [Validators.required]),
    storeEmail: new FormControl('', [Validators.required, Validators.email]),
    storeDescription: new FormControl('', [Validators.required, Validators.minLength(10)]),
    siteLocation: new FormControl('default', [Validators.required]),
  });

  isLoading = false;
  siteLocationOptions: { value: string; label: string }[] = [];

  constructor(
    private service: ServiceService,
    private locationContext: LocationContextService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    console.log('Add Store Component initialized with companyId:', this.companyId);
    this.loadSiteLocationOptions();
  }

  private loadSiteLocationOptions() {
    this.service.getSiteConfigurations().subscribe({
      next: (configs) => {
        const locations = [...new Set(configs.map(c => c.location).filter(Boolean))].sort();
        this.siteLocationOptions = locations.length > 0
          ? locations.map(loc => ({ value: loc, label: loc.charAt(0).toUpperCase() + loc.slice(1) }))
          : [{ value: 'default', label: 'Default' }];
        if (!this.addStoreForm.get('siteLocation')?.value) {
          this.addStoreForm.patchValue({
            siteLocation: this.locationContext.location || this.siteLocationOptions[0]?.value || 'default'
          });
        }
      },
      error: () => {
        this.siteLocationOptions = [{ value: 'default', label: 'Default' }];
        this.addStoreForm.patchValue({ siteLocation: 'default' });
      }
    });
  }

  onSubmit(){
    if (this.addStoreForm.valid && this.companyId) {
      this.isLoading = true;
      const formData = this.addStoreForm.value;
      
      console.log('Creating store with data:', formData);
      console.log('Company ID:', this.companyId);

      // Map form fields to Store schema
      // Combine address and city for location field
      const fullLocation = `${formData.storeAddress || ''}, ${formData.storeCity || ''}`;
      
      const storeData: any = {
        name: formData.storeName || '',
        location: fullLocation.trim(),
        siteLocation: formData.siteLocation || this.locationContext.location || 'default',
        phone: formData.storePhone || '',
        email: formData.storeEmail || '',
        description: formData.storeDescription || '',
        companyID: this.companyId,
      };
      
      console.log('Creating store with data:', storeData);

      this.service.createStore(storeData).subscribe({
        next: (store) => {
          console.log('Store created successfully:', store);
          this.snackBar.open('Store created successfully!', 'Close', { duration: 3000 });
          this.addStoreForm.reset();
          this.storeAdded.emit(); // Emit event to parent component
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error creating store:', error);
          this.snackBar.open('Failed to create store. Please try again.', 'Close', { duration: 5000 });
          this.isLoading = false;
        }
      });
    } else {
      console.warn('Form is invalid or companyId is missing:', {
        formValid: this.addStoreForm.valid,
        companyId: this.companyId
      });
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
    }
  }
}
